import * as React from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { Helmet } from 'react-helmet';
import { useDevCycleClient, withDevCycleProvider } from '@devcycle/react-client-sdk';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import config from '../config';
import { useVariableValue, type DVCVariableTypes } from './types/dvcVariableTypes';
import { getStore, useAppDispatch, useAppSelector } from './store/app';
import { LOGOUT_ACTION } from './reducers/consts';
import { importWithValidate } from './modules/utils';
import { setAuthenticatedUser, initGlobalStore, refreshUrlParams } from './actions/global';
import { setOnUnauthorized } from './modules/ApiClient';
import Analytics from './modules/analytics/Analytics';
import UserService from './modules/UserService';
import TooltipHost from './components/common/Tooltip/TooltipHost';
import ToastsHost from './components/common/Toast/ToastsHost';
import LoadingApp from './components/layout/LoadingApp';
import ErrorPage from './components/layout/ErrorPage/ErrorPage';
import logger from './modules/logging/logger';
import { CreateRedirectModal } from './components/CreateRedirectModal/CreateRedirectModal';
import { getScriptAttrs } from './lib/CustomerIo';
import useMount from './hooks/useMount';
import { useUpdateAppHeight } from './hooks/dom/useUpdateAppHeight';
import './styles/index.scss';
import { setShouldTransformAnalyticEvents } from './modules/global.utils';
import FeatureFlagSync from './components/FeatureFlag/FeatureFlagSync';
import { buildFeatureFlagUser } from './components/FeatureFlag/FeatureFlagUser';
import M1FeatureFlagSync from './components/FeatureFlag/M1FeatureFlagSync';

const AuthLayout = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'AuthLayout' */ './layouts/auth/AuthLayout')));
const ProjectLayout = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'ProjectLayout' */ './layouts/project/ProjectLayout')));
const DashboardLayout = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'DashboardLayout' */ './layouts/dashboard/DashboardLayout')));
const OrganizationLayout = React.lazy(() =>
    importWithValidate(() => import(/* webpackChunkName: 'OrganizationLayout' */ './layouts/organization/OrganizationLayout'))
);
const StudioPage = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'StudioLayout' */ './layouts/studio/StudioLayout')));
const PreviewErrorPage = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'PreviewErrorPage' */ './layouts/PreviewErrorPage')));
const SubscriptionLayout = React.lazy(() =>
    importWithValidate(() => import(/* webpackChunkName: 'SubscriptionLayout' */ './layouts/subscription/SubscriptionLayout'))
);
const ImportLayout = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'ImportLayout' */ './layouts/import/ImportLayout')));
const ImportCoreProjectLayout = React.lazy(() =>
    importWithValidate(() => import(/* webpackChunkName: 'ImportCoreProjectLayout' */ './layouts/import/ImportCoreProjectLayout'))
);
const ImportSelfHostedLayout = React.lazy(() =>
    importWithValidate(() => import(/* webpackChunkName: 'ImportSelfHostedLayout' */ './layouts/import/ImportSelfHostedLayout'))
);
const CreateLayout = React.lazy(() => importWithValidate(() => import(/* webpackChunkName: 'CreateLayout' */ './layouts/create/CreateLayout')));

export const history = createBrowserHistory();

Bugsnag.start({
    apiKey: config.bugsnag.apiKey,
    plugins: [new BugsnagPluginReact()]
});

const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

const getPostLoginRedirect = () => encodeURIComponent(window.location.href.replace(window.location.origin, ''));

const IS_PROD = config.env === 'prod';

const AnalyticsFeatureFlagSync = () => {
    const shouldTransformAnalyticEventNames = useVariableValue('project_kyurem_ui', false);

    React.useEffect(() => {
        setShouldTransformAnalyticEvents(shouldTransformAnalyticEventNames);
    }, [shouldTransformAnalyticEventNames]);

    return null;
};

const App = () => {
    const user = useAppSelector((state) => state.global.user);
    const pageTitle = useAppSelector((state) => state.global.pageTitle);
    const devcycleClient = useDevCycleClient<DVCVariableTypes>();

    const dispatch = useAppDispatch();

    const [isInitialLoading, setInitialLoading] = React.useState(true);
    const [postLoginRedirect, setPostLoginRedirect] = React.useState(getPostLoginRedirect());

    useUpdateAppHeight();

    useMount(() => {
        // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
        Analytics.page(true);

        dispatch(initGlobalStore({ history }));

        setOnUnauthorized(() => {
            UserService.onUnauthorized();

            // route to login page with current pathname as success redirect url
            const url = window.location.pathname + window.location.search;
            history.push(url || '/');
        });

        UserService.onAuthChanged((fetchedUser) => {
            if (fetchedUser?.createUser) {
                const { coreUser, createUser } = fetchedUser;
                // An edge case with Stackbit SSO users where the user could be logged into the Core app with a different email
                const userId = coreUser?.email === createUser.email ? coreUser?.id || createUser.id : createUser.id;

                Analytics.identify({ userId, previousId: userId === createUser.id ? undefined : createUser.id, user: createUser });
                devcycleClient.identifyUser(
                    buildFeatureFlagUser({
                        createUserId: createUser.id,
                        createUserEmail: createUser.email
                    })
                );
            } else {
                if (user) {
                    Analytics.reset();
                    devcycleClient.resetUser();
                } else {
                    devcycleClient.identifyUser({ isAnonymous: true });
                }
                getStore().dispatch({ type: LOGOUT_ACTION });
            }

            const isLoginUnified =
                fetchedUser?.coreUser?.email && fetchedUser?.createUser?.email && fetchedUser.coreUser.email === fetchedUser.createUser.email;

            dispatch(setAuthenticatedUser({ user: fetchedUser?.createUser ?? null, coreUser: isLoginUnified ? fetchedUser?.coreUser : null }));

            setInitialLoading(false);
        });

        UserService.refreshAuth();

        history.listen((location) => {
            dispatch(refreshUrlParams());
            Analytics.onRouteChange(location);
            setPostLoginRedirect(getPostLoginRedirect());
        });
    });

    const fetchUser = React.useCallback(() => {
        UserService.fetchCurrentUser().then(({ createUser, coreUser }) => {
            dispatch(
                setAuthenticatedUser({
                    user: createUser,
                    coreUser
                })
            );
        });
    }, [dispatch]);

    const siteTitle = React.useMemo(() => [pageTitle, 'Netlify Create', IS_PROD ? undefined : config.env].filter(Boolean).join(' - '), [pageTitle]);

    const scriptsAttrs = React.useMemo(() => [getScriptAttrs()].filter(Boolean), []);

    return (
        <Router history={history}>
            <ErrorBoundary
                onError={(error) => {
                    logger.error('App crash', { error });
                }}
                FallbackComponent={({ error }) => <ErrorPage error={error.message ?? ''} user={user} />}
            >
                <M1FeatureFlagSync />
                <AnalyticsFeatureFlagSync />
                <FeatureFlagSync />
                <React.Suspense fallback={<LoadingApp />}>
                    <Switch>
                        <Redirect exact from="/" to="/dashboard" />
                        {isInitialLoading ? <LoadingApp /> : null}

                        <Route path="/login" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/sso-login" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/register" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/forgot-password" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/reset-password" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/validate-email" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/quick-login" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/project/:id/accept-collaborator-invite" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/organization/:path*" render={({ match, location }) => <Redirect to={`/team/${match.params.path}${location.search}`} />} />
                        <Route path="/team/:id/invite/accept" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/logout" render={(props) => <AuthLayout {...props} />} />
                        <Route path="/studio/:id" render={(props) => <StudioPage {...props} fetchUser={fetchUser} />} />
                        <Route path="/project/:id/subscription/:tierId" render={(props) => <SubscriptionLayout {...props} />} />
                        <Route path="/project" render={(props) => <ProjectLayout {...props} />} />

                        {user ? null : <Redirect to={`/login?redirect=${postLoginRedirect}`} />}
                        <Route path="/create" render={(props) => <CreateLayout {...props} />} />
                        <Route path="/import/:data" render={(props) => <ImportCoreProjectLayout {...props} />} />
                        <Route path="/import" render={(props) => <ImportLayout {...props} />} />
                        <Route path="/import-self-hosted" render={(props) => <ImportSelfHostedLayout {...props} />} />

                        <Route path="/team/:id" render={(props) => <OrganizationLayout {...props} user={user} />} />
                        <Route path="/dashboard" render={(props) => <DashboardLayout {...props} fetchUser={fetchUser} />} />
                        <Route path="/account" render={(props) => <DashboardLayout {...props} />} />
                        <Route path="/local/:config" render={(props) => <DashboardLayout {...props} />} />
                        <Route path="/preview-error" render={(props) => <PreviewErrorPage {...props} user={user} />} />
                        <Redirect to="/dashboard" />
                    </Switch>
                </React.Suspense>

                <Helmet script={scriptsAttrs}>
                    <title>{siteTitle}</title>
                    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                    {!IS_PROD && <link rel="icon" type="image/svg+xml" href="/favicon-dev.svg" />}
                    {!IS_PROD && <link rel="alternate icon" type="image/png" href="/favicon-dev-32x32.png" sizes="32x32" />}
                    {!IS_PROD && <link rel="alternate icon" type="image/png" href="/favicon-dev-16x16.png" sizes="16x16" />}
                </Helmet>
                <TooltipHost />
                <ToastsHost />
                {!isInitialLoading && <CreateRedirectModal />}
            </ErrorBoundary>
        </Router>
    );
};

export default withDevCycleProvider({
    sdkKey: config.devCycle.sdkKey || 'fake-truthy-string',
    options: {
        apiProxyURL: 'https://featureflags.netlify.com',
        deferInitialization: true
    }
})(App);
